define('home/models/ActivityFeedItem',[
    'jquery',
    'underscore',
    'backbone',
    'core/api',

    'home/collections/PostCollection',

    'home/models/Thread',
    'home/models/Feed',

    'home/utils/datetimeUtils',
], function (
    $,
    _,
    Backbone,
    api,

    PostCollection,

    Thread,
    Feed,

    datetimeUtils
) {
    'use strict';

    /**
     * Represents 1 item in a feed. This item may contain multiple
     * activities rolled up.
     *
     * Default Attributes:
     *
     * verb: type of activity, ex: reply, follow
     * published: when this activity was created
     * totalActors: number of actors who performed this action
     *              ex: 40 users followed you, only have details for
     *              some number of them, ~ 3-5.
     * totalActivities: number of activities rolled up into this activity,
     *                  only have details for some number of them, ~ 3-5
     * score: the score used to determine the order of activities in a
     *        feed. this is the greater of the score naturally calculated
     *        on the backend based on user interaction and time, and the
     *        boosted score, which is a manually overridden value used to
     *        bump this activity up in a feed.
     * boostedScore: the manually overridden score
     * calculatedScore: the naturally calculated score
     * fixedUntil: an ISO string representation of the datetime
     *             when the boost should expire
     */
    var ActivityFeedItem = Backbone.Model.extend({
        initialize: function (_attributes, options) {
            // Some activity types don't have threads (ex: user followed user)
            if (options.thread)
                this.initializeRelated('thread', options, Thread);

            this.activities = options.activities;

            if (!this.id)
                this.id = ActivityFeedItem.buildId(this.activities);

            // While notifications only contain a single activity, just
            // return the unread status of that activity
            this.set({ isUnread: this.activities[0].get('isUnread') });

            this.handleBoostEnd();
            this.on('change:fixedUntil', this.handleBoostEnd);
        },

        parse: function (response) {
            // Break out the parts that make up the score object.
            // Store them separately, don't store score as an object,
            // since it's not useful for determining what attrs inside
            // are changing
            var boostAttrs = response.score;
            if (boostAttrs) {
                boostAttrs = {
                    // The score used to sort the feed
                    score: boostAttrs.value,
                    // The naturally calculated score (as if this was unboosted)
                    naturalScore: boostAttrs.calculated || boostAttrs.value,
                    // The boosted score, if boosted
                    boostedScore: boostAttrs.boost,
                    // The datetime that this boost expires, if boosted
                    fixedUntil: boostAttrs.fixedUntil,
                };
            }

            return _.extend(response, boostAttrs);
        },

        url: function (type) {
            return api.getURL('timelines/curation/' + this.urls[type]);
        },

        urls: {
            'boost': 'boostThreadInChannel',
            'unboost': 'unboostThreadInChannel',
        },

        sync: function (method, self, options) {
            options = options || {};
            options.url = self.url(method);

            // Must set processData to true, otherwise Backbone.sync sets it to false,
            // which results in a POST body of '[object Object]' to the API endpoint
            options.processData = true;

            // Must set emulateHTTP to true so that the request type will be POST instead
            // of PUT/DELETE/PATCH
            options.emulateHTTP = true;

            switch (method) {
            case 'boost':
                options.type = 'POST';
                options.data = _.extend({
                    score: self.get('score'),
                    fixedUntil: self.get('fixedUntil'),
                }, options.data);

                break;
            case 'unboost':
                options.type = 'POST';
                break;
            }

            options.data = _.defaults({
                thread: self.thread.id,
            }, options.data);

            return Backbone.sync(method, self, options);
        },

        toJSON: function () {
            return _.defaults({
                activities: _.invoke(this.activities, 'toJSON'),
                actors: _.invoke(this.actors, 'toJSON'),
                thread: this.thread && this.thread.toJSON(),
                relativeTime: this.getRelativeTimestamp(),
            },
            Backbone.Model.prototype.toJSON.apply(this, arguments));
        },

        getThread: function () {
            return this.thread;
        },

        getPosts: function () {
            if (!this.posts) {
                var postsFeedMeta = {
                    collectionClass: PostCollection,
                    collectionUrl: api.getURL('threads/listPosts'),
                };
                this.posts = {
                    desc: new Feed({
                        fetchOptions: {
                            thread: this.thread.id,
                            order: 'desc',
                        },
                    }, postsFeedMeta),
                    asc: new Feed({
                        fetchOptions: {
                            thread: this.thread.id,
                            order: 'asc',
                        },
                    }, postsFeedMeta),
                };
            }

            return this.posts;
        },

        /**
         * Returns whether or not this item has an image, whether from
         * rich media or pulled from embedly.
         * @returns {Boolean/Promise} - Either a boolean or a promise that resolves
         *                             with a boolean
         */
        hasThreadImage: function () {
            var image = $.Deferred();
            var thread = this.thread;

            if (!thread) {
                image.resolve(false);
            } else if (thread.getImage()) {
                image.resolve(true);
            } else if (thread.get('extending') === false) {
                image.resolve(false);
            } else {
                thread.on('change:extending', function (thread, isExtending) {
                    if (isExtending !== false)
                        return;

                    image.resolve(Boolean(thread.getImage()));
                });
            }

            return image.promise();
        },

        getBackendId: function () {
            // While feed items only contain a single activity, just return
            // the backend id of that activity
            return this.activities[0].get('backendId');
        },

        /**
         * Returns a string representing the timestamp for this feed item
         * relative to now. What the timestamp represents depends on the
         * type of activity this item represents. ex: trending items show
         * the time of the most recent comment on the trending thread,
         * comment items show the time of the comment.
         *
         * For rolled up activities, returns the time of the most recent activity.
         *
         * @returns {string} - Timestamp for this feed item relative to now.
         */
        getRelativeTimestamp: function () {
            return datetimeUtils.getRelativeDatetime(this.get('published'));
        },

        getRelativeBoostTimeRemaining: function () {
            var fixedUntil = this.get('fixedUntil');
            return fixedUntil && datetimeUtils.getRelativeDatetime(fixedUntil);
        },

        getSelectedPost: function () {
            return this.activities[0].get('selectedPost');
        },

        setSelectedPost: function (selectedPost) {
            this.activities[0].set({ selectedPost: selectedPost });
        },

        getThreadOverrides: function () {
            // When the server returns a threadOverrides object, it's as an empty
            // dictionary. We return an empty dictionary even if the threadOverrides
            // object is not set to maintain that expectation.
            return this.activities[0].get('threadOverrides') || {};
        },

        setThreadOverrides: function (threadOverrides) {
            this.activities[0].set({ threadOverrides: threadOverrides });
        },

        getPostsPreviewCollection: function () {
            if (!this.previewPostsCollection) {
                var selectedPost = this.getSelectedPost();
                var recentPosts = this.thread.recentPosts;

                this.previewPostsCollection = recentPosts.clone();
                if (selectedPost)
                    this.previewPostsCollection.add(selectedPost, { at: 0 });
            }

            return this.previewPostsCollection;
        },

        /**
         * When the boost expiration is reached, update the attributes on this model
         * to indicate that it is no longer boosted.
         * It's not necessary to call the unboost endpoint, since the backend will
         * unboost on its own due to the expiration having been reached.
         */
        handleBoostEnd: function () {
            if (this.boostEndTimeout) {
                clearTimeout(this.boostEndTimeout);
                this.boostEndTimeout = null;
            }

            if (!this.get('fixedUntil'))
                return;

            var boostEndMs = new Date(this.get('fixedUntil')).getTime();
            var boostEndRelativeMs = boostEndMs - new Date().getTime();

            if (boostEndRelativeMs < 1)
                this.unboost();
            else
                this.boostEndTimeout = setTimeout(_.bind(this.unboost, this), boostEndRelativeMs);
        },

        /**
         * Updates the attributes to boost this activity to the given score
         * @param  {number} newScore
         */
        boostInChannel: function (channelId, newScore, boostTimeMs) {
            // Determine the expiration by adding the given boost time to the current time,
            // or using the previous boost settings if not given a new boost time
            var fixedUntil = boostTimeMs ?
                new Date(new Date().getTime() + boostTimeMs).toISOString() :
                this.get('fixedUntil');

            this.set({
                boostedScore: newScore,
                fixedUntil: fixedUntil,
                score: newScore,
            });

            // If all the necessary boost details were given, call the boost endpoint.
            // Otherwise wait until user fills in all the details, which will trigger
            // a boost call.
            if (fixedUntil) {
                return this.sync('boost', this, {
                    data: {
                        channel: channelId,
                        fixedUntil: fixedUntil,
                    },
                });
            }
        },

        /**
         * Unboosts this item by removing the boost attributes. Does not call endpoint
         * to unboost on the backend.
         * This can be called from an unboost handler which also calls the endpoint,
         * and when the boost expiration has been reached.
         */
        unboost: function () {
            this.set({
                score: this.get('naturalScore'),
                boostedScore: null,
                fixedUntil: null,
            });
        },

        unboostInChannel: function (channelId) {
            this.unboost();

            return this.sync('unboost', this, {
                data: {
                    channel: channelId,
                },
            });
        },

    }, {
        buildId: function (activities) {
            return _.pluck(activities, 'id').join('|');
        },

        // Default time a card should maintain its boosted score
        DEFAULT_BOOST_TIME_MS: 1000 * 60 * 60, // 1 hour

        // Default frequency a card should halve its score after boost time ends
        DEFAULT_DECAY_RATE_SEC: 60 * 30, // 30 min
    });

    return ActivityFeedItem;
});

